/**
 * Episode View Module
 * Orchestrates the full-page episode view with audio player and transcription
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { formatTime, escapeHtml } from '../core/utils.js';
import { createLoadingElement } from '../components/loading.js';

// Import sub-modules
import { renderTranscription, renderTranslatedTranscription, changeTranscriptFont, toggleTranscriptionFormat, seekToTime, scrollToCurrentSegment } from './episode-transcription.js';
import { renderMetadataSidebar, switchSidebarTab, toggleSection } from './episode-sidebar.js';
import { searchInTranscription, clearTranscriptionSearch, navigateMatch, setEpisodeSearchQuery, restoreSearch } from './episode-search.js';
import { addTag, removeTag, showTagSuggestions, handleTagInput, selectTagSuggestion, hideSuggestions } from './episode-tags.js';
import { sendChatMessage, handleChatKeydown, newChat, saveChat, loadSavedChat, deleteSavedChat, analyzeEpisode, toggleChatMaximize, clearChat, toggleChatDropdown, startNewChat, switchToCurrentChat, switchToSavedChat, toggleModelDropdown, selectModel } from './episode-chat.js';

// Track current state
let currentEpisodeId = null;
let isViewOpen = false;

// Translation state
let activeTranslation = null;
let showingTranslation = false;

// Cached model list for translate dropdown
let cachedModelList = null;

// Track playing episode (persists across navigation)
window.playingEpisodeId = null;
window.playingEpisodeData = null;

// Available playback speeds
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
let currentSpeedIndex = 2; // Default to 1x

// Debounced position sync to backend
let positionSyncTimeout = null;
function syncPositionToBackend(episodeId, position) {
    // Clear any pending sync
    if (positionSyncTimeout) clearTimeout(positionSyncTimeout);

    // Debounce: wait 2 seconds before syncing to avoid too many requests
    positionSyncTimeout = setTimeout(async () => {
        try {
            await API.put(`/api/episodes/${episodeId}/playback-position`, { position });
        } catch (error) {
            console.warn('Failed to sync playback position:', error);
        }
    }, 2000);
}

// Immediately sync position (used on pause)
async function syncPositionImmediately(episodeId, position) {
    if (positionSyncTimeout) clearTimeout(positionSyncTimeout);
    try {
        await API.put(`/api/episodes/${episodeId}/playback-position`, { position });
    } catch (error) {
        console.warn('Failed to sync playback position:', error);
    }
}

/**
 * Open episode in full-page view
 */
export async function openEpisodeView(episodeId) {
    try {
        if (window.location.hash !== `#/episode/${episodeId}`) {
            history.pushState({ episodeId }, '', `#/episode/${episodeId}`);
        }

        const view = document.getElementById('episode-detail-view');
        const mainContainer = document.querySelector('.main-container');

        if (!view) {
            console.error('Episode detail view not found');
            return;
        }

        if (mainContainer) mainContainer.style.display = 'none';
        view.style.display = 'block';
        isViewOpen = true;

        // Show loading with audio waveform animation
        const headerContent = document.getElementById('episode-header-content');
        if (headerContent) {
            headerContent.textContent = '';
            headerContent.appendChild(createLoadingElement());
        }
        
        const transcriptionEl = document.getElementById('episode-transcription');
        if (transcriptionEl) transcriptionEl.textContent = '';
        const metadataEl = document.getElementById('episode-metadata');
        if (metadataEl) metadataEl.textContent = '';

        // Fire both fetches in parallel — HTTP/2 multiplexes them on one connection.
        // The shell is small (~5 KB gzipped) and renders chrome immediately;
        // the transcription body is heavy and streams in behind it.
        const shellPromise = API.get(`/api/episodes/${episodeId}`);
        const transcriptionPromise = API.get(`/api/episodes/${episodeId}/transcription`);

        const episode = await shellPromise;
        if (currentEpisodeId !== episodeId && currentEpisodeId !== null) {
            // User navigated away before shell arrived
            return;
        }
        currentEpisodeId = episodeId;
        window.currentEpisodeId = episodeId;
        window.currentEpisodeData = episode;
        activeTranslation = null;
        showingTranslation = false;

        renderEpisodeHeader(episode);
        renderTranscriptionToolbar(episode);
        renderTranscriptionPlaceholder(episode);
        renderTranslationToggles(episode);
        renderMetadataSidebar(episode);
        restoreSearch();

        // Merge transcription payload when it arrives and re-render the body.
        transcriptionPromise.then(transcription => {
            if (window.currentEpisodeId !== episodeId) return;  // navigated away
            Object.assign(episode, {
                transcription: transcription.transcription,
                transcription_data: transcription.transcription_data,
                speaker_names: transcription.speaker_names,
            });
            window.currentEpisodeData = episode;
            renderTranscription(episode);
            renderTranscriptionToolbar(episode);
            renderMetadataSidebar(episode);
        }).catch(err => {
            console.error('Failed to load transcription body:', err);
            const container = document.getElementById('episode-transcription');
            if (container && window.currentEpisodeId === episodeId) {
                container.textContent = 'Failed to load transcription. Please retry.';
            }
        });

        // Only load audio if different episode (preserve playback when returning)
        if (window.playingEpisodeId !== episodeId) {
            if (episode.downloaded && episode.local_file_path) {
                loadGlobalAudioPlayer(episodeId);
            } else if (episode.audio_url) {
                loadGlobalAudioPlayerStream(episode.audio_url);
            }
        } else {
            // Same episode - just make sure player is visible
            const player = document.getElementById('global-audio-player');
            if (player) player.classList.add('active');
            // Scroll transcript to current playback position after a short delay
            setTimeout(() => scrollToCurrentSegment(), 100);
        }

        setupEpisodeViewKeyboardShortcuts();

    } catch (error) {
        console.error('Error opening episode view:', error);
        showNotification('Failed to load episode', 'error');
        closeEpisodeView();
    }
}

/**
 * Close the full-page episode view
 */
export function closeEpisodeView() {
    const view = document.getElementById('episode-detail-view');
    const mainContainer = document.querySelector('.main-container');
    const audioPlayer = document.getElementById('global-audio-player');
    const audio = document.getElementById('global-audio');

    if (view) view.style.display = 'none';
    if (mainContainer) mainContainer.style.display = 'block';

    // Only hide player if audio is paused or not loaded
    // Keep audio playing when navigating away!
    if (audioPlayer && audio && (audio.paused || !audio.src)) {
        audioPlayer.classList.remove('active');
    }
    // Don't pause audio - let it continue playing

    // Reset mobile drawer state
    document.getElementById('episode-sidebar')?.classList.remove('mobile-expanded');
    document.getElementById('sidebar-overlay')?.classList.remove('active');
    document.getElementById('mobile-menu-btn')?.classList.remove('active');
    document.body.style.overflow = '';

    currentEpisodeId = null;
    isViewOpen = false;
    activeTranslation = null;
    showingTranslation = false;
    // Keep playingEpisodeId and playingEpisodeData - they persist across navigation
    window.currentEpisodeData = null;
    window.currentEpisodeId = null;

    if (window.location.hash.startsWith('#/episode/')) {
        history.pushState(null, '', window.location.pathname);
    }
}

/**
 * Render episode header section
 */
function renderTranscriptionPlaceholder(episode) {
    const container = document.getElementById('episode-transcription');
    if (!container) return;
    container.textContent = '';
    if (!episode.has_transcription) {
        const div = document.createElement('div');
        div.className = 'no-transcription';
        const p1 = document.createElement('p');
        p1.textContent = 'No transcription available.';
        const p2 = document.createElement('p');
        p2.textContent = 'Use "Fetch Transcription" to retrieve it from the server.';
        div.appendChild(p1);
        div.appendChild(p2);
        container.appendChild(div);
        return;
    }
    const loading = document.createElement('div');
    loading.className = 'transcription-loading';
    loading.style.cssText = 'padding: 2rem; text-align: center; color: var(--text-secondary);';
    loading.textContent = 'Loading transcription…';
    container.appendChild(loading);
}

function renderEpisodeHeader(episode) {
    const container = document.getElementById('episode-header-content');
    if (!container) return;

    const hasSpeakerData = episode.has_diarization ?? (episode.transcription_data?.segments_with_speakers?.length > 0);
    const hasTranscription = episode.has_transcription ?? !!episode.transcription;
    const tags = episode.tags || [];

    container.innerHTML = `
        <h1 class="episode-title">${escapeHtml(episode.title)}</h1>
        <div class="episode-meta">
            ${episode.podcast_title ? `<span class="podcast-name">${escapeHtml(episode.podcast_title)}</span>` : ''}
            ${episode.published_date ? `<span class="episode-date">${new Date(episode.published_date).toLocaleDateString()}</span>` : ''}
            ${episode.duration_seconds ? `<span class="episode-duration">${Math.floor(episode.duration_seconds / 60)} min</span>` : ''}
        </div>
        <div class="episode-status-row">
            <div class="status-indicators">
                <span class="status-badge ${episode.downloaded ? 'active' : 'inactive'}">
                    ${episode.downloaded ? '✓' : '○'} Downloaded
                </span>
                <span class="status-badge ${episode.processed ? 'active' : 'inactive'}">
                    ${episode.processed ? '✓' : '○'} Transcribed
                </span>
                <span class="status-badge ${hasSpeakerData ? 'active' : 'inactive'}">
                    ${hasSpeakerData ? '✓' : '○'} Speakers
                </span>
            </div>
            <div class="compact-actions">
                <button class="btn btn-small"
                        id="played-toggle-btn"
                        onclick="togglePlayedStatusInView(${episode.id}, ${episode.played || false})"
                        style="background: ${episode.played ? 'var(--success)' : 'var(--bg-dark)'};">
                    ${episode.played ? '✓ Played' : '○ Mark as Played'}
                </button>
                ${!episode.downloaded ? `<button class="btn btn-small" onclick="downloadEpisode(${episode.id})">Download</button>` : ''}
                ${!hasTranscription ? `
                    <button class="btn btn-small btn-secondary" onclick="fetchTranscription(${episode.id}, false)">
                        Transcribe
                    </button>
                ` : ''}
                ${episode.downloaded && !hasSpeakerData ? `
                    <button class="btn btn-small btn-secondary" onclick="redoDiarization(${episode.id})">
                        Diarise
                    </button>
                ` : ''}
                ${hasTranscription && episode.llm_status !== 'completed' ? `
                    <button class="btn btn-small btn-secondary" onclick="rerunAIAnalysis(${episode.id})">
                        Analyze
                    </button>
                ` : ''}
            </div>
        </div>
        <div class="episode-tags-row">
            <div class="tag-chips" id="episode-tag-chips">
                ${tags.map(tag => `
                    <span class="tag-chip" data-tag="${escapeHtml(tag)}">
                        ${escapeHtml(tag)}
                        <button class="tag-remove" onclick="removeTag('${escapeHtml(tag).replace(/'/g, "\\'")}')">×</button>
                    </span>
                `).join('')}
            </div>
            <div class="tag-input-wrapper">
                <input type="text"
                       id="tag-input"
                       class="tag-input"
                       placeholder="Add tag..."
                       onkeydown="handleTagInput(event)"
                       oninput="showTagSuggestions(this.value)">
                <div id="tag-suggestions" class="tag-suggestions"></div>
            </div>
        </div>
    `;
}

/**
 * Render transcription toolbar
 * Note: Uses innerHTML for complex toolbar layout - all content is static/developer-controlled, no user input is injected unsanitized.
 */
function renderTranscriptionToolbar(episode) {
    const container = document.getElementById('transcription-toolbar');
    if (!container) return;

    const hasSpeakerData = episode.has_diarization ?? (episode.transcription_data?.segments_with_speakers?.length > 0);
    const hasTranscription = episode.has_transcription ?? !!(episode.transcription || hasSpeakerData);

    // Build cached translations list (composite keys: lang__model_short)
    const translations = episode.translations || {};
    const cachedKeys = Object.keys(translations);
    let cachedHtml = '';
    if (cachedKeys.length > 0) {
        cachedHtml = `<div class="cached-translations"><h5>Cached</h5>` +
            cachedKeys.map(cacheKey => {
                const t = translations[cacheKey];
                const safeName = escapeHtml(t.language_name || t.language || cacheKey);
                const modelShort = t.model ? escapeHtml(t.model.split('/').pop()) : '';
                const displayLabel = modelShort && modelShort !== 'default'
                    ? `${safeName} (${modelShort})`
                    : safeName;
                return `<div class="cached-lang-item">
                    <span onclick="loadCachedTranslation('${escapeHtml(cacheKey)}')">${displayLabel}</span>
                    <button onclick="deleteCachedTranslation('${escapeHtml(cacheKey)}')" title="Delete">✕</button>
                </div>`;
            }).join('') + `</div>`;
    }

    // Note: innerHTML used here with only static developer-controlled content
    container.innerHTML = `
        <div class="transcription-search">
            <input type="text"
                   id="episode-search-input"
                   placeholder="Search in transcription..."
                   class="episode-search-input"
                   onkeyup="if(event.key === 'Enter') searchInTranscription()">
            <button class="btn btn-small btn-secondary" onclick="searchInTranscription()" title="Search">
                🔍
            </button>
            <span id="search-match-count" class="search-match-count" style="display: none;"></span>
            <button class="btn btn-small btn-secondary" onclick="navigateMatch(-1)" title="Previous" id="prev-match-btn" style="display: none;">
                ▲
            </button>
            <button class="btn btn-small btn-secondary" onclick="navigateMatch(1)" title="Next" id="next-match-btn" style="display: none;">
                ▼
            </button>
            <button class="btn btn-small btn-secondary" onclick="clearTranscriptionSearch()" title="Clear" id="clear-search-btn" style="display: none;">
                ✕
            </button>
        </div>
        <div class="transcription-actions">
            ${hasSpeakerData ? `
                <button class="btn btn-small btn-secondary" onclick="toggleTranscriptionFormat()" id="format-toggle-btn">
                    Raw
                </button>
            ` : ''}
            <button class="btn btn-small btn-secondary" onclick="copyTranscription()">
                Copy
            </button>
            ${hasTranscription ? `
                <div class="transcript-dropdown" id="download-dropdown">
                    <button class="btn btn-small btn-secondary" onclick="toggleDownloadDropdown()">Download ▾</button>
                    <div class="transcript-dropdown-menu" id="download-dropdown-menu">
                        <div class="transcript-dropdown-item" onclick="downloadTranscript('txt')">Plain Text (.txt)</div>
                        <div class="transcript-dropdown-item" onclick="downloadTranscript('srt')">SRT Subtitles (.srt)</div>
                        <div class="transcript-dropdown-item" onclick="downloadTranscript('json')">Raw JSON (.json)</div>
                    </div>
                </div>
                <div class="transcript-dropdown" id="translate-dropdown">
                    <button class="btn btn-small btn-secondary" onclick="toggleTranslateDropdown()">Translate ▾</button>
                    <div class="transcript-dropdown-menu translate-menu" id="translate-dropdown-menu">
                        <label>Language</label>
                        <select class="translate-select" id="translate-language">
                            <option value="es" data-name="Spanish">Spanish</option>
                            <option value="fr" data-name="French">French</option>
                            <option value="de" data-name="German">German</option>
                            <option value="pt" data-name="Portuguese">Portuguese</option>
                            <option value="it" data-name="Italian">Italian</option>
                            <option value="ja" data-name="Japanese">Japanese</option>
                            <option value="ko" data-name="Korean">Korean</option>
                            <option value="zh" data-name="Chinese">Chinese</option>
                            <option value="ru" data-name="Russian">Russian</option>
                            <option value="ar" data-name="Arabic">Arabic</option>
                        </select>
                        <label>Model</label>
                        <select class="translate-select" id="translate-model">
                            <option value="">Loading models...</option>
                        </select>
                        <div class="translate-mode-toggle">
                            <label><input type="radio" name="translate-mode" value="segments" checked> Segments</label>
                            <label><input type="radio" name="translate-mode" value="full"> Full text</label>
                        </div>
                        <div class="translate-actions">
                            <button class="btn btn-small" id="translate-btn" onclick="translateTranscript()">Translate</button>
                        </div>
                        ${cachedHtml}
                    </div>
                </div>
            ` : ''}
        </div>
    `;
}

/**
 * Load audio into global player (downloaded file)
 */
function loadGlobalAudioPlayer(episodeId) {
    const player = document.getElementById('global-audio-player');
    const audio = document.getElementById('global-audio');

    if (!player || !audio) return;

    audio.src = `/api/episodes/${episodeId}/audio`;
    player.classList.add('active');

    // Track what's playing (persists across navigation)
    window.playingEpisodeId = episodeId;
    window.playingEpisodeData = window.currentEpisodeData;

    setupGlobalPlayerControls();
}

/**
 * Load audio into global player (streaming URL)
 */
function loadGlobalAudioPlayerStream(audioUrl) {
    const player = document.getElementById('global-audio-player');
    const audio = document.getElementById('global-audio');

    if (!player || !audio) return;

    audio.src = audioUrl;
    player.classList.add('active');

    // Track what's playing (persists across navigation)
    window.playingEpisodeId = currentEpisodeId;
    window.playingEpisodeData = window.currentEpisodeData;

    setupGlobalPlayerControls();
}

/**
 * Setup global audio player controls
 */
function setupGlobalPlayerControls() {
    const audio = document.getElementById('global-audio');
    const playBtn = document.querySelector('.global-audio-player .play-btn');
    const speedBtn = document.querySelector('.global-audio-player .speed-btn');
    const progressContainer = document.querySelector('.global-audio-player .progress-container');
    const progressBar = document.querySelector('.global-audio-player .progress-bar');
    const currentTimeEl = document.querySelector('.global-audio-player .current-time');
    const durationEl = document.querySelector('.global-audio-player .duration-time');

    if (!audio) return;

    if (playBtn) {
        playBtn.onclick = () => {
            if (audio.paused) audio.play();
            else audio.pause();
        };
    }

    if (speedBtn) {
        speedBtn.onclick = () => {
            currentSpeedIndex = (currentSpeedIndex + 1) % PLAYBACK_SPEEDS.length;
            const speed = PLAYBACK_SPEEDS[currentSpeedIndex];
            audio.playbackRate = speed;
            speedBtn.textContent = speed === 1 ? '1x' : `${speed}x`;
        };
    }

    const skipBack = document.querySelector('.global-audio-player .skip-back');
    const skipForward = document.querySelector('.global-audio-player .skip-forward');

    if (skipBack) {
        skipBack.onclick = () => {
            audio.currentTime = Math.max(0, audio.currentTime - 15);
        };
    }

    if (skipForward) {
        skipForward.onclick = () => {
            audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + 15);
        };
    }

    audio.onplay = () => { if (playBtn) playBtn.textContent = '⏸'; };
    audio.onpause = () => {
        if (playBtn) playBtn.textContent = '▶';
        // Save playback position on pause (sync to backend + localStorage fallback)
        const episodeId = window.playingEpisodeId || currentEpisodeId;
        if (episodeId && audio.currentTime > 0) {
            localStorage.setItem(`audio-position-${episodeId}`, audio.currentTime.toString());
            syncPositionImmediately(episodeId, audio.currentTime);
        }
    };

    audio.ontimeupdate = () => {
        if (currentTimeEl) currentTimeEl.textContent = formatTime(audio.currentTime);
        if (progressBar && audio.duration) {
            progressBar.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
        }
        // Periodically save position during playback (every 10 seconds)
        const episodeId = window.playingEpisodeId || currentEpisodeId;
        if (episodeId && !audio.paused && Math.floor(audio.currentTime) % 10 === 0) {
            localStorage.setItem(`audio-position-${episodeId}`, audio.currentTime.toString());
            syncPositionToBackend(episodeId, audio.currentTime);
        }
    };

    audio.onloadedmetadata = () => {
        if (durationEl) durationEl.textContent = formatTime(audio.duration);

        // Restore saved position - prefer backend (syncs across devices) over localStorage
        const episodeId = window.playingEpisodeId || currentEpisodeId;
        if (episodeId) {
            // First check backend position from episode data
            const backendPosition = window.currentEpisodeData?.playback_position || 0;
            const localPosition = parseFloat(localStorage.getItem(`audio-position-${episodeId}`) || '0');

            // Use whichever is more recent (higher position usually means more recent)
            const position = Math.max(backendPosition, localPosition);

            // Only restore if position is valid and not at the very end
            if (position > 0 && position < audio.duration - 5) {
                audio.currentTime = position;
                // Scroll transcript to restored position after a short delay
                setTimeout(() => scrollToCurrentSegment(), 200);
            }
        }
    };

    if (progressContainer) {
        progressContainer.onclick = (e) => {
            const rect = progressContainer.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            audio.currentTime = percent * audio.duration;
        };
    }
}

/**
 * Setup keyboard shortcuts for episode view
 */
function setupEpisodeViewKeyboardShortcuts() {
    const handler = (e) => {
        if (!isViewOpen) return;
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        const audio = document.getElementById('global-audio');

        switch(e.key) {
            case 'Escape':
                closeEpisodeView();
                break;
            case ' ':
                if (audio) {
                    e.preventDefault();
                    if (audio.paused) audio.play();
                    else audio.pause();
                }
                break;
            case 'ArrowLeft':
                if (audio) {
                    e.preventDefault();
                    audio.currentTime = Math.max(0, audio.currentTime - 10);
                }
                break;
            case 'ArrowRight':
                if (audio) {
                    e.preventDefault();
                    audio.currentTime = Math.min(audio.duration || 0, audio.currentTime + 10);
                }
                break;
        }
    };

    document.removeEventListener('keydown', window._episodeViewKeyHandler);
    window._episodeViewKeyHandler = handler;
    document.addEventListener('keydown', handler);
}

/**
 * Check if episode view is currently open
 */
export function isEpisodeViewOpen() {
    return isViewOpen;
}

/**
 * Toggle mobile sidebar visibility (slide-over drawer)
 */
function toggleMobileSidebar() {
    const sidebar = document.getElementById('episode-sidebar');
    const overlay = document.getElementById('sidebar-overlay');
    const menuBtn = document.getElementById('mobile-menu-btn');

    if (!sidebar) return;

    const isExpanded = sidebar.classList.toggle('mobile-expanded');

    // Toggle overlay
    overlay?.classList.toggle('active', isExpanded);

    // Toggle hamburger animation
    menuBtn?.classList.toggle('active', isExpanded);

    // Lock body scroll when drawer is open
    document.body.style.overflow = isExpanded ? 'hidden' : '';
}

/**
 * Get current episode ID
 */
export function getCurrentEpisodeId() {
    return currentEpisodeId;
}

/**
 * Re-run AI analysis for an episode (summary, tags, keywords, speakers, etc.)
 */
export async function rerunAIAnalysis(episodeId) {
    const { showNotification } = await import('../components/notifications.js');
    const { showConfirmModal } = await import('../components/modal.js');

    try {
        const confirmed = await showConfirmModal({
            title: 'Re-run AI Analysis',
            message: 'This will re-analyze the episode to extract summary, tags, keywords, book mentions, concepts, and speaker names.',
            confirmText: 'Run Analysis',
            cancelText: 'Cancel',
            icon: '🤖'
        });

        if (!confirmed) return;

        showNotification('Running AI analysis...', 'info');

        const response = await fetch(`/api/episodes/${episodeId}/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                extractions: {
                    summary: true,
                    tags: true,
                    keywords: true,
                    books: true,
                    links: true,
                    ideas: true,
                    concepts: true,
                    speakers: true
                }
            })
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Analysis failed');
        }

        const result = await response.json();
        showNotification('AI analysis complete!', 'success');

        // Refresh the episode view to show updated data
        await openEpisodeView(episodeId);

    } catch (error) {
        console.error('Error in rerunAIAnalysis:', error);
        showNotification(`Error: ${error.message}`, 'error');
    }
}

/**
 * Toggle played status in the episode detail view
 */
async function togglePlayedStatusInView(episodeId, currentStatus) {
    const button = document.getElementById('played-toggle-btn');
    if (!button) return;

    button.disabled = true;

    try {
        const formData = new FormData();
        formData.append('played', !currentStatus);

        const response = await fetch(`/api/episodes/${episodeId}/played`, {
            method: 'PUT',
            body: formData
        });

        if (response.ok) {
            const result = await response.json();
            const newStatus = result.played;

            // Update button appearance
            button.textContent = newStatus ? '✓ Played' : '○ Mark as Played';
            button.style.background = newStatus ? 'var(--success)' : 'var(--bg-dark)';
            button.onclick = () => togglePlayedStatusInView(episodeId, newStatus);

            showNotification(newStatus ? 'Marked as played' : 'Marked as unplayed', 'success');
        } else {
            showNotification('Failed to update played status', 'error');
        }
    } catch (error) {
        console.error('Error toggling played status:', error);
        showNotification('Error updating played status', 'error');
    } finally {
        button.disabled = false;
    }
}

// ========================================
// Download & Translation functions
// ========================================

/**
 * Toggle download dropdown
 */
function toggleDownloadDropdown() {
    const menu = document.getElementById('download-dropdown-menu');
    const translateMenu = document.getElementById('translate-dropdown-menu');
    if (translateMenu) translateMenu.classList.remove('active');
    if (menu) menu.classList.toggle('active');
}

/**
 * Toggle translate dropdown - fetches model list on first open
 */
function toggleTranslateDropdown() {
    const menu = document.getElementById('translate-dropdown-menu');
    const downloadMenu = document.getElementById('download-dropdown-menu');
    if (downloadMenu) downloadMenu.classList.remove('active');
    if (menu) {
        const isOpening = !menu.classList.contains('active');
        menu.classList.toggle('active');
        if (isOpening) populateTranslateModels();
    }
}

/**
 * Fetch and populate model list for translate dropdown
 */
async function populateTranslateModels() {
    const select = document.getElementById('translate-model');
    if (!select) return;

    // Use cached list if available
    if (cachedModelList) {
        renderModelOptions(select, cachedModelList);
        return;
    }

    try {
        const data = await API.get('/api/llm/models');
        cachedModelList = data.models || [];
        renderModelOptions(select, cachedModelList);
    } catch (error) {
        console.error('Failed to fetch models:', error);
        select.textContent = '';
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = '(default model)';
        select.appendChild(opt);
    }
}

function renderModelOptions(select, models) {
    select.textContent = '';
    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = '(default model)';
    select.appendChild(defaultOpt);
    models.forEach(m => {
        const opt = document.createElement('option');
        opt.value = m.id;
        opt.textContent = m.name;
        select.appendChild(opt);
    });
}

/**
 * Download transcript in specified format
 */
async function downloadTranscript(format) {
    const episodeId = currentEpisodeId;
    if (!episodeId) return;

    // Close dropdown
    const menu = document.getElementById('download-dropdown-menu');
    if (menu) menu.classList.remove('active');

    try {
        let url = `/api/episodes/${episodeId}/download-transcript?format=${format}`;
        // If showing a translation, download translated version using composite cache key
        if (showingTranslation && activeTranslation) {
            const langParam = activeTranslation.cache_key || activeTranslation.language;
            url += `&lang=${encodeURIComponent(langParam)}`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Download failed');

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Extract filename from Content-Disposition or generate one
        const disposition = response.headers.get('Content-Disposition');
        let filename = `transcript.${format}`;
        if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match) filename = match[1];
        }

        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);

        showNotification(`Downloaded ${format.toUpperCase()}`, 'success');
    } catch (error) {
        console.error('Download error:', error);
        showNotification('Failed to download transcript', 'error');
    }
}

/**
 * Translate transcript using LLM
 */
async function translateTranscript() {
    const episodeId = currentEpisodeId;
    if (!episodeId) return;

    const langSelect = document.getElementById('translate-language');
    const modelSelect = document.getElementById('translate-model');
    const modeRadio = document.querySelector('input[name="translate-mode"]:checked');
    const translateBtn = document.getElementById('translate-btn');

    if (!langSelect || !modeRadio) return;

    const language = langSelect.value;
    const languageName = langSelect.options[langSelect.selectedIndex].dataset.name;
    const mode = modeRadio.value;
    const model = modelSelect?.value || undefined;

    // Show loading state
    if (translateBtn) {
        translateBtn.disabled = true;
        translateBtn.textContent = 'Translating...';
    }

    try {
        const payload = { language, language_name: languageName, mode };
        if (model) payload.model = model;

        const response = await fetch(`/api/episodes/${episodeId}/translate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Translation failed');
        }

        const result = await response.json();
        const cacheKey = result.cache_key || language;
        activeTranslation = result;
        showingTranslation = true;

        // Update cached data using composite cache key
        if (window.currentEpisodeData) {
            if (!window.currentEpisodeData.translations) {
                window.currentEpisodeData.translations = {};
            }
            window.currentEpisodeData.translations[cacheKey] = result;
        }

        // Render translated view
        renderTranslatedView(result);

        // Re-render toolbar to update cached list and toggle buttons
        renderTranscriptionToolbar(window.currentEpisodeData);
        renderTranslationToggles(window.currentEpisodeData);

        // Close dropdown
        const menu = document.getElementById('translate-dropdown-menu');
        if (menu) menu.classList.remove('active');

        showNotification(`Translated to ${languageName}`, 'success');
    } catch (error) {
        console.error('Translation error:', error);
        showNotification(`Translation failed: ${error.message}`, 'error');
    } finally {
        if (translateBtn) {
            translateBtn.disabled = false;
            translateBtn.textContent = 'Translate';
        }
    }
}

/**
 * Render translated transcription view
 */
function renderTranslatedView(translation) {
    const container = document.getElementById('episode-transcription');
    if (!container || !window.currentEpisodeData) return;

    renderTranslatedTranscription(container, translation, window.currentEpisodeData);
}

/**
 * Toggle between original and translated view
 */
function toggleTranslationView() {
    if (!window.currentEpisodeData) return;

    if (showingTranslation) {
        // Show original
        showingTranslation = false;
        activeTranslation = null;
        renderTranscription(window.currentEpisodeData);
    } else if (activeTranslation) {
        // Show translation
        showingTranslation = true;
        renderTranslatedView(activeTranslation);
    }
    renderTranslationToggles(window.currentEpisodeData);
}

/**
 * Render translation toggle pill buttons above the transcript
 */
function renderTranslationToggles(episode) {
    const transcriptionEl = document.getElementById('episode-transcription');
    if (!transcriptionEl) return;

    // Remove existing toggles
    const existing = document.getElementById('translation-toggles');
    if (existing) existing.remove();

    const translations = episode?.translations || {};
    const keys = Object.keys(translations);
    if (keys.length === 0) return;

    const container = document.createElement('div');
    container.id = 'translation-toggles';
    container.className = 'translation-toggles';

    // "Original" button
    const origBtn = document.createElement('button');
    origBtn.className = 'translation-toggle-btn' + (!showingTranslation ? ' active' : '');
    origBtn.textContent = 'Original';
    origBtn.onclick = () => {
        showingTranslation = false;
        activeTranslation = null;
        renderTranscription(window.currentEpisodeData);
        renderTranslationToggles(window.currentEpisodeData);
    };
    container.appendChild(origBtn);

    // One button per cached translation
    keys.forEach(cacheKey => {
        const t = translations[cacheKey];
        const langName = t.language_name || t.language || cacheKey;
        const modelShort = t.model ? t.model.split('/').pop() : '';
        const label = modelShort && modelShort !== 'default'
            ? `${langName} (${modelShort})`
            : langName;

        const btn = document.createElement('button');
        btn.className = 'translation-toggle-btn' +
            (showingTranslation && activeTranslation?.cache_key === cacheKey ? ' active' : '');
        btn.textContent = label;
        btn.onclick = () => loadCachedTranslation(cacheKey);
        container.appendChild(btn);
    });

    // Insert before the transcription content
    transcriptionEl.parentNode.insertBefore(container, transcriptionEl);
}

/**
 * Load a cached translation by composite cache key
 */
function loadCachedTranslation(cacheKey) {
    if (!window.currentEpisodeData?.translations?.[cacheKey]) return;

    const translation = window.currentEpisodeData.translations[cacheKey];
    activeTranslation = translation;
    showingTranslation = true;
    renderTranslatedView(translation);
    renderTranslationToggles(window.currentEpisodeData);

    // Close dropdown
    const menu = document.getElementById('translate-dropdown-menu');
    if (menu) menu.classList.remove('active');

    const modelShort = translation.model ? translation.model.split('/').pop() : '';
    const label = translation.language_name || cacheKey;
    const suffix = modelShort && modelShort !== 'default' ? ` (${modelShort})` : '';
    showNotification(`Loaded ${label}${suffix} translation`, 'success');
}

/**
 * Delete a cached translation by composite cache key
 */
async function deleteCachedTranslation(cacheKey) {
    const episodeId = currentEpisodeId;
    if (!episodeId) return;

    try {
        const response = await fetch(`/api/episodes/${episodeId}/translations/${encodeURIComponent(cacheKey)}`, {
            method: 'DELETE'
        });

        if (!response.ok) throw new Error('Delete failed');

        // Update local data
        if (window.currentEpisodeData?.translations) {
            delete window.currentEpisodeData.translations[cacheKey];
        }

        // If we're currently showing this translation, switch back to original
        if (showingTranslation && activeTranslation?.cache_key === cacheKey) {
            showingTranslation = false;
            activeTranslation = null;
            renderTranscription(window.currentEpisodeData);
        }

        // Re-render toolbar and toggles to update cached list
        renderTranscriptionToolbar(window.currentEpisodeData);
        renderTranslationToggles(window.currentEpisodeData);

        showNotification('Translation deleted', 'success');
    } catch (error) {
        console.error('Delete translation error:', error);
        showNotification('Failed to delete translation', 'error');
    }
}

// Close dropdowns when clicking outside
document.addEventListener('click', (e) => {
    if (!e.target.closest('#download-dropdown')) {
        const menu = document.getElementById('download-dropdown-menu');
        if (menu) menu.classList.remove('active');
    }
    if (!e.target.closest('#translate-dropdown')) {
        const menu = document.getElementById('translate-dropdown-menu');
        if (menu) menu.classList.remove('active');
    }
});

// Re-export functions for window global access
export {
    searchInTranscription, clearTranscriptionSearch, navigateMatch, setEpisodeSearchQuery,
    addTag, removeTag, showTagSuggestions, handleTagInput, selectTagSuggestion, hideSuggestions,
    sendChatMessage, handleChatKeydown, newChat, saveChat, loadSavedChat, deleteSavedChat, analyzeEpisode,
    toggleChatMaximize, clearChat, toggleChatDropdown, startNewChat, switchToCurrentChat, switchToSavedChat,
    toggleModelDropdown, selectModel,
    changeTranscriptFont, toggleTranscriptionFormat, seekToTime,
    switchSidebarTab, toggleSection, toggleMobileSidebar, togglePlayedStatusInView,
    toggleDownloadDropdown, downloadTranscript, toggleTranslateDropdown, translateTranscript,
    toggleTranslationView, loadCachedTranslation, deleteCachedTranslation, renderTranslationToggles
};

// Setup window globals for onclick handlers
window.searchInTranscription = searchInTranscription;
window.clearTranscriptionSearch = clearTranscriptionSearch;
window.navigateMatch = navigateMatch;
window.addTag = (tag) => addTag(tag, currentEpisodeId);
window.removeTag = (tag) => removeTag(tag, currentEpisodeId);
window.showTagSuggestions = showTagSuggestions;
window.handleTagInput = handleTagInput;
window.selectTagSuggestion = selectTagSuggestion;
window.sendChatMessage = () => sendChatMessage(currentEpisodeId);
window.handleChatKeydown = handleChatKeydown;
window.newChat = (id) => newChat(id, openEpisodeView);
window.saveChat = (id) => saveChat(id, openEpisodeView);
window.loadSavedChat = loadSavedChat;
window.deleteSavedChat = (id, idx) => deleteSavedChat(id, idx, openEpisodeView);
window.analyzeEpisode = (id) => analyzeEpisode(id, getCurrentEpisodeId, openEpisodeView);
window.toggleChatMaximize = toggleChatMaximize;
window.clearChat = clearChat;
window.toggleChatDropdown = toggleChatDropdown;
window.startNewChat = startNewChat;
window.switchToCurrentChat = switchToCurrentChat;
window.switchToSavedChat = switchToSavedChat;
window.toggleModelDropdown = toggleModelDropdown;
window.selectModel = selectModel;
window.changeTranscriptFont = changeTranscriptFont;
window.toggleTranscriptionFormat = toggleTranscriptionFormat;
window.seekToTime = seekToTime;
window.switchSidebarTab = switchSidebarTab;
window.toggleSection = toggleSection;
window.toggleMobileSidebar = toggleMobileSidebar;
window.rerunAIAnalysis = rerunAIAnalysis;
window.togglePlayedStatusInView = togglePlayedStatusInView;
window.toggleDownloadDropdown = toggleDownloadDropdown;
window.downloadTranscript = downloadTranscript;
window.toggleTranslateDropdown = toggleTranslateDropdown;
window.translateTranscript = translateTranscript;
window.toggleTranslationView = toggleTranslationView;
window.loadCachedTranslation = loadCachedTranslation;
window.deleteCachedTranslation = deleteCachedTranslation;
window.currentEpisodeId = null;
